import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Search, Loader2, Palette, ArrowLeft } from 'lucide-react';
import { getProducts } from '../api/product.api';

const FabricLibraryView = ({ setActiveView, showNotification }) => {
  const [fabrics, setFabrics] = useState([]);
  const [isLoadingFabrics, setIsLoadingFabrics] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [fabricSearchTerm, setFabricSearchTerm] = useState('');
  const [fabricPage, setFabricPage] = useState(1);
  const [hasMoreFabrics, setHasMoreFabrics] = useState(false);
  const [selectedFabric, setSelectedFabric] = useState(null);
  const loadMoreRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isHandlingPopState = useRef(false);
  const hasInitialHistoryPush = useRef(false);

  // Fetch fabrics from library (optimized with useCallback)
  const fetchFabrics = useCallback(async (page = 1, search = '', append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoadingFabrics(true);
    }
    
    try {
      const response = await getProducts(page, 20, search, { hasCollection: 'false' });
      const fetchedFabrics = response?.products || [];
      
      if (append) {
        setFabrics(prev => {
          // Prevent duplicates
          const existingIds = new Set(prev.map(f => f._id));
          const newFabrics = fetchedFabrics.filter(f => !existingIds.has(f._id));
          return [...prev, ...newFabrics];
        });
      } else {
        setFabrics(fetchedFabrics);
      }
      
      setHasMoreFabrics(response?.pagination?.hasNextPage || false);
      setFabricPage(page);
    } catch (error) {
      console.error('Error fetching fabrics:', error);
      showNotification?.('Failed to load fabrics', 'error');
    } finally {
      setIsLoadingFabrics(false);
      setIsLoadingMore(false);
    }
  }, [showNotification]);

  // Load fabrics on mount and push history state
  useEffect(() => {
    setFabrics([]);
    setFabricPage(1);
    fetchFabrics(1, fabricSearchTerm, false);
    
    // Push history state when Fabric Library view is opened
    if (!hasInitialHistoryPush.current) {
      window.history.pushState(
        { view: 'fabric-library' },
        '',
        window.location.pathname
      );
      hasInitialHistoryPush.current = true;
    }
  }, [fetchFabrics]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFabrics([]);
      setFabricPage(1);
      fetchFabrics(1, fabricSearchTerm, false);
    }, 500);

    return () => clearTimeout(timer);
  }, [fabricSearchTerm, fetchFabrics]);

  // Infinite scroll with Intersection Observer
  useEffect(() => {
    if (!hasMoreFabrics || isLoadingMore || isLoadingFabrics || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry.isIntersecting && hasMoreFabrics && !isLoadingMore && !isLoadingFabrics) {
          const nextPage = fabricPage + 1;
          fetchFabrics(nextPage, fabricSearchTerm, true);
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '200px',
        threshold: 0.1
      }
    );

    const currentLoadMoreRef = loadMoreRef.current;
    observer.observe(currentLoadMoreRef);

    return () => {
      if (currentLoadMoreRef) {
        observer.unobserve(currentLoadMoreRef);
      }
    };
  }, [hasMoreFabrics, isLoadingMore, isLoadingFabrics, fabricPage, fabricSearchTerm, fetchFabrics]);

  // Handle fabric selection
  const handleSelectFabric = useCallback((fabric) => {
    // If this is for the callback (AI Studio integration), handle it
    const callbackData = sessionStorage.getItem('fabricLibraryCallback');
    if (callbackData) {
      try {
        const { type } = JSON.parse(callbackData);
        
        // Store selected fabric in sessionStorage as fallback
        sessionStorage.setItem('selectedFabric', JSON.stringify({
          fabric,
          callbackType: type,
          timestamp: Date.now()
        }));
        
        // Dispatch custom event with fabric and callback type
        window.dispatchEvent(new CustomEvent('fabricSelected', { 
          detail: { 
            fabric,
            callbackType: type
          } 
        }));
        
        // Clear the callback
        sessionStorage.removeItem('fabricLibraryCallback');
        
        // Small delay to ensure event is processed before navigation
        setTimeout(() => {
          // Navigate back
          setActiveView('aistudio');
          showNotification?.('Fabric selected successfully', 'success');
        }, 100);
      } catch (error) {
        console.error('Error selecting fabric:', error);
        showNotification?.('Failed to select fabric', 'error');
      }
    } else {
      // Otherwise, show fabric detail view
      setSelectedFabric(fabric);
      // Push state to browser history
      window.history.pushState(
        { view: 'fabric-detail', fabricId: fabric._id },
        '',
        window.location.pathname
      );
    }
  }, [setActiveView, showNotification]);

  // Fabric Item Component
  const FabricItem = React.memo(({ fabric, onSelect }) => {
    const [isInView, setIsInView] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const imgRef = useRef(null);

    useEffect(() => {
      const currentRef = imgRef.current;
      if (!currentRef) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsInView(true);
              observer.disconnect();
            }
          });
        },
        { rootMargin: '50px' }
      );

      observer.observe(currentRef);

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
      };
    }, []);

    // Extract fabric image URL from various possible properties
    const fabricImage = fabric.image || 
                       fabric.imageUrl || 
                       fabric.images?.original?.s3Url || 
                       fabric.images?.original?.imgbbUrl || 
                       fabric.images?.original?.url || 
                       '';
    const fabricName = fabric.name || 'Untitled Fabric';

    return (
      <button
        onClick={() => onSelect(fabric)}
        className="group bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-purple-400 hover:shadow-lg transition-all cursor-pointer text-left w-full"
      >
        <div 
          ref={imgRef}
          className="aspect-square bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center relative overflow-hidden"
        >
          {isInView && fabricImage && !imageError ? (
            <>
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
                </div>
              )}
              <img
                src={fabricImage}
                alt={fabricName}
                className={`w-full h-full object-cover group-hover:scale-105 transition-transform ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setImageLoaded(true)}
                onError={() => {
                  setImageError(true);
                  setImageLoaded(false);
                }}
                loading="lazy"
              />
            </>
          ) : (
            <div className="text-center px-4">
              <Palette className="w-12 h-12 mx-auto text-gray-400 mb-2" />
              <p className="text-xs text-gray-500">{imageError ? 'Failed to load' : 'No Image'}</p>
            </div>
          )}
        </div>
        <div className="p-3">
          <h4 className="font-semibold text-gray-800 truncate text-sm mb-1">
            {fabricName}
          </h4>
          {fabric.description && (
            <p className="text-xs text-gray-500 line-clamp-2">
              {fabric.description}
            </p>
          )}
        </div>
      </button>
    );
  });

  FabricItem.displayName = 'FabricItem';

  // Handle closing fabric detail
  const handleCloseFabric = useCallback(() => {
    if (isHandlingPopState.current) {
      isHandlingPopState.current = false;
      return;
    }
    setSelectedFabric(null);
    // Only go back if there's history
    if (window.history.state?.view === 'fabric-detail') {
      window.history.back();
    }
  }, []);

  // Handle back button from fabric library
  const handleBackToAiStudio = useCallback(() => {
    if (isHandlingPopState.current) {
      isHandlingPopState.current = false;
      return;
    }
    setActiveView('aistudio');
    // Only go back if there's history
    if (window.history.state?.view === 'fabric-library') {
      window.history.back();
    }
  }, [setActiveView]);

  // Listen for browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      isHandlingPopState.current = true;
      
      if (selectedFabric) {
        // If fabric detail is open, close it
        setSelectedFabric(null);
      } else {
        // If on fabric library main view, go back to AI Studio
        setActiveView('aistudio');
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedFabric, setActiveView]);

  // Show fabric detail view if a fabric is selected
  if (selectedFabric) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-hidden">
        <div className="h-full w-full bg-white overflow-y-auto">
          {/* Fabric detail content would go here - for now just a placeholder */}
          <div className="p-4">
            <button
              onClick={handleCloseFabric}
              className="mb-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              ← Back to Library
            </button>
            <h2 className="text-2xl font-bold">{selectedFabric.name}</h2>
            <p className="text-gray-600">{selectedFabric.description}</p>
            {selectedFabric.image && (
              <img 
                src={selectedFabric.image} 
                alt={selectedFabric.name}
                className="mt-4 max-w-md rounded-lg"
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-hidden">
      <div className="h-full w-full flex flex-col bg-white">
        {/* Header with Gradient */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 sm:px-6 pb-4 pt-[max(1rem,env(safe-area-inset-top))] flex items-center justify-between shadow-lg flex-shrink-0">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <button
              onClick={handleBackToAiStudio}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-white/20 transition-colors active:scale-95"
              title="Go back"
              aria-label="Go back"
            >
              <ArrowLeft size={24} />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg border border-white/30">
                <Palette className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">Fabric Library</h1>
                <p className="text-sm text-purple-100">Choose from your uploaded fabrics</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search fabrics..."
              value={fabricSearchTerm}
              onChange={(e) => setFabricSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Fabrics Grid with Infinite Scroll */}
        <div 
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
        {isLoadingFabrics && fabrics.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : fabrics.length === 0 ? (
          <div className="text-center py-12">
            <Palette className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <p className="text-base text-gray-600">
              {fabricSearchTerm ? 'No fabrics found matching your search' : 'No fabrics available'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {fabrics.map((fabric) => (
                <FabricItem
                  key={fabric._id}
                  fabric={fabric}
                  onSelect={handleSelectFabric}
                />
              ))}
            </div>
            
            {/* Infinite Scroll Trigger Element */}
            {hasMoreFabrics && (
              <div 
                ref={loadMoreRef}
                className="flex items-center justify-center py-4"
              >
                {isLoadingMore && (
                  <div className="flex items-center gap-2 text-purple-600">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Loading more fabrics...</span>
                  </div>
                )}
              </div>
            )}
            
            {/* End of list indicator */}
            {!hasMoreFabrics && fabrics.length > 0 && (
              <div className="text-center py-4">
                <p className="text-xs text-gray-500">All fabrics loaded</p>
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default FabricLibraryView;

